import React, { useEffect, useRef, useState } from "react";
import "./ShareholdingPattern.css";
import Testimonial from "../../../Testimonial/Testimonial";
import { Helmet } from "react-helmet";

const ShareholdingPattern = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    {
      name: "Artificial Intelligence",
      logo: "/logos/artificial-intelligence.png",
    },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    // Set initial container width
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1; // Adjust speed as needed (pixels per frame)

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  const [activeTab, setActiveTab] = useState("visual");
  const [animationKey, setAnimationKey] = useState(0);

  // Sample data - in a real app, this would come from an API
  const shareholdingData = [
    {
      category: "Director 1",
      percentage: 75,
      shares: 6250000,
    },
    {
      category: "Director 2",
      percentage: 25,
      shares: 1870000,
    },
    // {
    //   category: "Domestic Institutional Investors",
    //   percentage: 8.3,
    //   shares: 830000,
    // },
    // { category: "Public & Others", percentage: 0, shares: 1050000 },
  ];

  // Colors for the pie chart
  const colors = ["#4e73df", "#1cc88a", "#36b9cc", "#f6c23e"];

  useEffect(() => {
    // Re-trigger animations when tab changes
    setAnimationKey((prevKey) => prevKey + 1);
  }, [activeTab]);

  // Calculate pie chart segments
  const calculateDashArray = (percentage) => {
    const circumference = 2 * Math.PI * 40;
    return `${(percentage / 100) * circumference} ${circumference}`;
  };

  return (
    <div>
      <Helmet>
        <title>
          Shareholding Pattern Technology | Equity Management Solutions
        </title>
        <meta
          name="description"
          content="Advanced shareholding pattern technology solutions including equity tracking systems, shareholder analytics, and ownership structure visualization tools for corporate governance."
        />
        <meta
          name="keywords"
          content="shareholding pattern technology, equity management, shareholder tracking, ownership structure, investor analytics, stock ownership, corporate share registry, capital structure, beneficial ownership, securities management"
        />
      </Helmet>
      <div className="mk-eshp-container">
        <h2 className="mk-eshp-title">MK Etech Shareholding Pattern</h2>

        <div className="mk-eshp-tabs">
          <button
            className={`mk-eshp-tab ${activeTab === "visual" ? "active" : ""}`}
            onClick={() => setActiveTab("visual")}
          >
            Visual Representation
          </button>
          <button
            className={`mk-eshp-tab ${
              activeTab === "detailed" ? "active" : ""
            }`}
            onClick={() => setActiveTab("detailed")}
          >
            Detailed Table
          </button>
        </div>

        <div className="mk-eshp-content">
          {activeTab === "visual" ? (
            <div key={animationKey} className="mk-eshp-visual">
              <div className="mk-eshp-pie-chart">
                <svg viewBox="0 0 100 100" className="mk-eshp-svg">
                  {shareholdingData.map((item, index) => {
                    const offset = shareholdingData
                      .slice(0, index)
                      .reduce(
                        (sum, data) =>
                          sum + (data.percentage / 100) * 2 * Math.PI * 40,
                        0
                      );
                    return (
                      <circle
                        key={index}
                        className="mk-eshp-pie-segment"
                        cx="50"
                        cy="50"
                        r="40"
                        fill="transparent"
                        stroke={colors[index]}
                        strokeWidth="20"
                        strokeDasharray={calculateDashArray(item.percentage)}
                        strokeDashoffset={-offset}
                      />
                    );
                  })}
                </svg>
                <div className="mk-eshp-pie-center">
                  <span className="mk-eshp-total-shares">1,00,000</span>
                  <span className="mk-eshp-total-text">Total Shares</span>
                </div>
              </div>

              <div className="mk-eshp-legend">
                {shareholdingData.map((item, index) => (
                  <div key={index} className="mk-eshp-legend-item">
                    <div
                      className="mk-eshp-legend-color"
                      style={{ backgroundColor: colors[index] }}
                    ></div>
                    <div className="mk-eshp-legend-text">
                      <span className="mk-eshp-legend-category">
                        {item.category}
                      </span>
                      <span className="mk-eshp-legend-percentage">
                        {item.percentage}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div key={animationKey} className="mk-eshp-detailed">
              <table className="mk-eshp-table">
                <thead>
                  <tr>
                    <th className="mk-eshp-th">Category</th>
                    <th className="mk-eshp-th">Percentage</th>
                    <th className="mk-eshp-th">Number of Shares</th>
                  </tr>
                </thead>
                <tbody>
                  {shareholdingData.map((item, index) => (
                    <tr key={index} className="mk-eshp-tr">
                      <td className="mk-eshp-td">{item.category}</td>
                      <td className="mk-eshp-td">
                        <div className="mk-eshp-percentage-bar-container">
                          <div
                            className="mk-eshp-percentage-bar"
                            style={{
                              width: `${item.percentage}%`,
                              backgroundColor: colors[index],
                            }}
                          ></div>
                          <span className="mk-eshp-percentage-text">
                            {item.percentage}%
                          </span>
                        </div>
                      </td>
                      <td className="mk-eshp-td">
                        {item.shares.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="mk-eshp-tfoot">
                  <tr>
                    <td className="mk-eshp-td mk-eshp-total">Total</td>
                    <td className="mk-eshp-td mk-eshp-total">100%</td>
                    <td className="mk-eshp-td mk-eshp-total">10,000,000</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareholdingPattern;
